import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { RotateCcw, Type } from 'lucide-react';
import {
  fetchTexte,
  saveTexte,
  type TextDefinition,
} from '@/api/betrieb.api';

// ─── Texte und Titel ────────────────────────────────────────────────────────
//
// Was hier steht, sieht jeder Besucher. Deshalb zwei Vorkehrungen:
//
//   1. Ein leeres Feld heißt „Vorgabe benutzen", nicht „nichts anzeigen".
//      Sonst wäre die Anwendung nach einem versehentlich geleerten Feld
//      namenlos, und niemand käme auf die Idee, dass das an dieser Seite liegt.
//   2. Die Vorgabe steht als Platzhalter im Feld. Man sieht also, was ohne
//      eigenen Text erscheinen würde – ohne ihn erst löschen zu müssen.

export function TextePage() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'texts'],
    queryFn: fetchTexte,
  });

  /** Der Bearbeitungsstand. Leer = Vorgabe. */
  const [stand, setStand] = useState<Record<string, string>>({});

  useEffect(() => {
    if (data) setStand(data.werte);
  }, [data]);

  const speichern = useMutation({
    mutationFn: () => saveTexte(stand),
    onSuccess: (werte) => {
      queryClient.setQueryData(['admin', 'texts'], (alt: unknown) =>
        alt && typeof alt === 'object' ? { ...(alt as object), werte } : alt,
      );
      setStand(werte);
      toast.success('Texte gespeichert.');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const gruppen = useMemo(() => {
    const m = new Map<string, TextDefinition[]>();
    for (const d of data?.definitionen ?? []) {
      const liste = m.get(d.gruppe) ?? [];
      liste.push(d);
      m.set(d.gruppe, liste);
    }
    return [...m.entries()];
  }, [data?.definitionen]);

  if (isLoading) {
    return <div className="p-4 text-sm text-slate-500 sm:p-6">Texte werden geladen…</div>;
  }

  if (isError || !data) {
    return (
      <div className="p-4 sm:p-6">
        <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error instanceof Error ? error.message : 'Texte konnten nicht geladen werden.'}
        </p>
      </div>
    );
  }

  const unveraendert =
    JSON.stringify(stand) === JSON.stringify(data.werte);

  const setze = (key: string, wert: string) => {
    setStand((s) => {
      const neu = { ...s };
      if (wert.trim() === '') delete neu[key];
      else neu[key] = wert;
      return neu;
    });
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4 sm:p-6">
      <header>
        <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-900 sm:text-2xl">
          <Type className="h-5 w-5 text-slate-400" aria-hidden="true" />
          Texte &amp; Titel
        </h1>
        <p className="mt-0.5 text-sm text-slate-500">
          Was die Anwendung Ihren Kunden sagt. Ein leeres Feld bedeutet: die
          Vorgabe gilt – sie steht als blasser Text im Feld.
        </p>
      </header>

      {gruppen.map(([gruppe, felder]) => (
        <section key={gruppe} className="rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="text-sm font-semibold text-slate-900">{gruppe}</h2>
          <div className="mt-4 space-y-4">
            {felder.map((d) => {
              const wert = stand[d.key] ?? '';
              const abweichend = wert.trim() !== '';
              return (
                <div key={d.key}>
                  <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
                    <label htmlFor={`text-${d.key}`} className="text-xs font-medium text-slate-700">
                      {d.label}
                    </label>
                    {abweichend && (
                      <button
                        onClick={() => setze(d.key, '')}
                        className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600"
                      >
                        <RotateCcw className="h-3 w-3" aria-hidden="true" />
                        auf Vorgabe zurück
                      </button>
                    )}
                  </div>

                  {d.lang ? (
                    <textarea
                      id={`text-${d.key}`}
                      rows={3}
                      value={wert}
                      placeholder={d.vorgabe || 'ohne Vorgabe – bleibt leer'}
                      onChange={(e) => setze(d.key, e.target.value)}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                  ) : (
                    <input
                      id={`text-${d.key}`}
                      value={wert}
                      placeholder={d.vorgabe || 'ohne Vorgabe – bleibt leer'}
                      onChange={(e) => setze(d.key, e.target.value)}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                  )}

                  <p className="mt-1 text-xs text-slate-400">{d.ort}</p>

                  {/* Platzhalter nennen, statt sie raten zu lassen. Wer nicht
                      weiß, dass es {stammbaum} gibt, schreibt den Namen fest
                      hinein – und dann steht in jeder Mail derselbe. */}
                  {d.platzhalter && d.platzhalter.length > 0 && (
                    <p className="mt-1 text-xs text-slate-500">
                      Platzhalter:{' '}
                      {d.platzhalter.map((p, i) => (
                        <span key={p}>
                          {i > 0 && ', '}
                          <code className="rounded bg-slate-100 px-1 py-0.5 text-[11px] text-slate-700">
                            {'{' + p + '}'}
                          </code>
                        </span>
                      ))}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}

      <div className="sticky bottom-4 flex justify-end">
        <button
          onClick={() => speichern.mutate()}
          disabled={speichern.isPending || unveraendert}
          className="rounded-md bg-blue-600 px-5 py-2.5 text-sm font-medium text-white shadow-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {speichern.isPending ? 'Wird gespeichert…' : 'Alle Texte speichern'}
        </button>
      </div>
    </div>
  );
}

export default TextePage;
